import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Typography, Container, TextField, Button,
    Paper, Grid, CircularProgress, IconButton, Card, CardContent,
    Divider, Stack, Chip, Menu, MenuItem, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
    Save as SaveIcon, Close as CloseIcon,
    ArrowBack as ArrowBackIcon, Add as AddIcon,
    AccessTime as TimeIcon, Group as GroupIcon, LocationOn as MapIcon,
    Delete as DeleteIcon, AddCircleOutline as AddQtyIcon, RemoveCircleOutline as RemoveIcon,
    Flag as EndPointIcon, CalendarMonth as CalIcon,
    DirectionsBus, DirectionsWalk, DirectionsBike,
} from '@mui/icons-material';
import axios       from "../api/axiosConfig";
import axiosPublic from "../api/axiosPublic";
import { useTranslation } from "react-i18next";
import { useAuth }     from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { amountToCents, centsToAmount } from "../utils/currency";
import MiniCalendar from "./MiniCalendar";

const TERRACOTTA = '#C4622D';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';
const WALNUT     = '#2C1F14';

const JS_TO_BIT = [6, 0, 1, 2, 3, 4, 5];

// Bitmask day labels: bit 0 = Mon … bit 6 = Sun
const DAY_LABELS = [
    { bit: 0, short: 'M',  name: 'Mon' },
    { bit: 1, short: 'T',  name: 'Tue' },
    { bit: 2, short: 'W',  name: 'Wed' },
    { bit: 3, short: 'T',  name: 'Thu' },
    { bit: 4, short: 'F',  name: 'Fri' },
    { bit: 5, short: 'S',  name: 'Sat' },
    { bit: 6, short: 'S',  name: 'Sun' },
];

/** Compact day-of-week toggle row — wired to the activeDays bitmask */
function DayPicker({ value = 127, onChange }) {
    const toggle = (bit) => {
        const mask = 1 << bit;
        const next = value ^ mask;
        if (next === 0) return; // prevent deselecting all days
        onChange(next);
    };
    return (
        <Stack direction="row" spacing={0.4} sx={{ mt: 0.4 }}>
            {DAY_LABELS.map(d => {
                const active = (value & (1 << d.bit)) !== 0;
                return (
                    <Box key={d.bit} onClick={() => toggle(d.bit)}
                         sx={{
                             width: 28, height: 28, borderRadius: '50%',
                             display: 'flex', alignItems: 'center', justifyContent: 'center',
                             fontSize: '0.7rem', fontWeight: 800, cursor: 'pointer',
                             transition: '0.15s',
                             bgcolor: active ? TERRACOTTA : 'transparent',
                             color: active ? '#fff' : TERRACOTTA,
                             border: `1.5px solid ${active ? TERRACOTTA : 'rgba(196,98,45,0.35)'}`,
                             '&:hover': { bgcolor: active ? '#9A4A1F' : 'rgba(196,98,45,0.1)' },
                         }}
                         title={d.name}>
                        {d.short}
                    </Box>
                );
            })}
        </Stack>
    );
}

/** Summarise bitmask to short string, e.g. "Mon–Fri" or "Mon, Wed, Fri" */
function daysSummary(mask) {
    if (mask === 127) return 'Every day';
    if (mask === 31)  return 'Mon–Fri';
    if (mask === 96)  return 'Sat–Sun';
    const names = DAY_LABELS.filter(d => (mask & (1 << d.bit)) !== 0).map(d => d.name);
    return names.join(', ');
}

function fmt(t) { if (!t) return ""; return String(t).split(':').slice(0, 2).join(':'); }
function toISODate(d) {
    if (!d) return null;
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

export default function TourDetails() {
    const { id }   = useParams();
    const { t }    = useTranslation();
    const { user } = useAuth();
    const { format: formatMoney, convert, exchangeRate } = useCurrency();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');
    const [tour,    setTour]    = useState(null);
    const [ratingSummary, setRatingSummary] = useState(null);

    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [booking, setBooking] = useState({
        local:   { ADULT: { normal: 0, electric: 0 }, CHILD: { normal: 0, electric: 0 }, INFANT: { normal: 0, electric: 0 } },
        foreign: { ADULT: { normal: 0, electric: 0 }, CHILD: { normal: 0, electric: 0 }, INFANT: { normal: 0, electric: 0 } },
    });

    const isManager = user?.role === 'MANAGER' || user?.role === 'ADMIN';
    const [activeMainEdit, setActiveMainEdit] = useState(null);
    const [tempMainValue,  setTempMainValue]  = useState("");
    const [imageFile,      setImageFile]      = useState(null);
    const [imagePreview,   setImagePreview]   = useState(null);
    const [editStop,         setEditStop]         = useState({ index: null, data: null });
    const [stopImageFile,    setStopImageFile]    = useState(null);
    const [stopImagePreview, setStopImagePreview] = useState(null);
    const [editSlot,  setEditSlot]  = useState({ index: null, data: null });
    const [editPrice, setEditPrice] = useState({ index: null, data: null });
    const [iconAnchor, setIconAnchor] = useState(null);

    const [deleteOpen,        setDeleteOpen]        = useState(false);
    const [deletePassword,    setDeletePassword]    = useState('');
    const [deleteLoading,     setDeleteLoading]     = useState(false);
    const [deleteError,       setDeleteError]       = useState('');
    const [deactivatedMsg,    setDeactivatedMsg]    = useState('');
    const [statusLoading,     setStatusLoading]     = useState(false);

    const fetchTour = useCallback(async () => {
        try {
            const r = await axiosPublic.get(`/api/tourOffers/view/${id}`);
            setTour(r.data);
            setLoading(false);
            // Fetch rating summary (public endpoint — no auth needed)
            axiosPublic.get(`/api/ratings/summary?targetId=${id}&targetType=TOUR_OFFER`)
                .then(res => setRatingSummary(res.data))
                .catch(() => {});
        }
        catch { setError("Could not find this tour."); setLoading(false); }
    }, [id]);

    useEffect(() => { fetchTour(); }, [fetchTour]);

    const tourActiveDays = (tour?.slots || []).reduce((acc, s) => acc | (s.activeDays ?? 127), 0);

    const slotsForDate = (date) => {
        if (!date || !tour?.slots) return [];
        const bit = JS_TO_BIT[date.getDay()];
        return tour.slots.filter(s => ((s.activeDays ?? 127) & (1 << bit)) !== 0);
    };

    const handleDateChange = (date) => { setSelectedDate(date); setSelectedSlot(null); };
    const rawSlots = slotsForDate(selectedDate);
    const availableSlots = (() => {
        if (!selectedDate) return rawSlots;
        const now = new Date();
        const isToday = selectedDate.getFullYear() === now.getFullYear() &&
                        selectedDate.getMonth()    === now.getMonth()    &&
                        selectedDate.getDate()     === now.getDate();
        if (!isToday) return rawSlots;
        const nowMin = now.getHours() * 60 + now.getMinutes();
        return rawSlots.filter(slot => {
            const parts = (slot.startTime || '').toString().split(':').map(Number);
            return parts[0] * 60 + (parts[1] || 0) > nowMin;
        });
    })();

    const handleBookingCount = (type, category, bikeKind, delta) => {
        const curr = Object.values(booking.local).reduce((s, v) => s + v.normal + v.electric, 0)
            + Object.values(booking.foreign).reduce((s, v) => s + v.normal + v.electric, 0);
        if (delta > 0 && curr >= (tour?.maxGroupSize || 99)) return;
        setBooking(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [category]: {
                    ...prev[type][category],
                    [bikeKind]: Math.max(0, prev[type][category][bikeKind] + delta),
                },
            },
        }));
    };

    const calculateTotal = () => {
        if (!tour?.prices) return 0;
        const surchargeRate = tour.type === 'BIKE' ? 0.30 : 0;
        let t = 0;
        ['local','foreign'].forEach(type => {
            const isForeign = type === 'foreign';
            Object.keys(booking[type]).forEach(cat => {
                const p = tour.prices.find(p => p.category === cat && p.isForeigner === isForeign);
                const price = p ? p.price : 0;
                const { normal, electric } = booking[type][cat];
                t += price * (normal + electric);
                t += Math.round(price * surchargeRate) * electric;
            });
        });
        return t;
    };

    const totalElectric = Object.values(booking.local).reduce((s, v) => s + v.electric, 0)
        + Object.values(booking.foreign).reduce((s, v) => s + v.electric, 0);
    const total = calculateTotal();

    const summaryLines = [];
    ['local','foreign'].forEach(type => {
        ['ADULT','CHILD','INFANT'].forEach(cat => {
            const { normal, electric } = booking[type][cat];
            const isForeign = type === 'foreign';
            const p = (tour?.prices||[]).find(p => p.category === cat && p.isForeigner === isForeign);
            const price = p?.price || 0;
            const prefix = type === 'local' ? 'Res.' : 'Tou.';
            const catLabel = { ADULT: t('tour.adult_label', 'Adult (18+)'), CHILD: t('tour.child_label', 'Child (7–17)'), INFANT: t('tour.infant_label', 'Infant (0–6)') }[cat];
            if (normal > 0) {
                summaryLines.push({ label: `${normal}× ${prefix} ${catLabel}`, amount: price * normal, unitPrice: price });
            }
            if (electric > 0) {
                const surcharge = tour?.type === 'BIKE' ? Math.round(price * 0.30) : 0;
                summaryLines.push({
                    label: `${electric}× ${prefix} ${catLabel}${tour?.type === 'BIKE' ? ' (E-Bike +30%)' : ''}`,
                    amount: (price + surcharge) * electric,
                    unitPrice: price + surcharge,
                });
            }
        });
    });

    const handleGoToCheckout = () => {
        if (!selectedDate || !selectedSlot) return;
        const tourCurrency = tour.currency || 'EUR';
        const priceMap = { local: {}, foreign: {} };
        (tour.prices||[]).forEach(p => { priceMap[p.isForeigner ? 'foreign' : 'local'][p.category] = p.price; });
        navigate(`/checkout/${id}`, { state: {
                title: tour.title, total, booking, prices: priceMap,
                tourCurrency,
                tourType:  tour.type,
                tourDate:  toISODate(selectedDate),
                startTime: fmt(selectedSlot.startTime),
                endTime:   fmt(selectedSlot.endTime),
                electricBikes: totalElectric,
            }});
    };

    // ── Manager actions ───────────────────────────────────────────────────
    const saveMainField = async (field, value) => {
        try {
            const u = { ...tour, [field]: value };
            const fd = new FormData();
            fd.append("tourOffer", new Blob([JSON.stringify(u)], { type:"application/json" }));
            await axios.put(`/api/tourOffers/edit/${id}`, fd, { headers:{ 'Content-Type':'multipart/form-data' } });
            setTour(u); setActiveMainEdit(null); setIconAnchor(null);
        } catch { alert("Update failed."); }
    };

    const saveImage = async () => {
        try {
            const fd = new FormData();
            fd.append("tourOffer", new Blob([JSON.stringify(tour)], { type:"application/json" }));
            fd.append("image", imageFile);
            await axios.put(`/api/tourOffers/edit/${id}`, fd, { headers:{ 'Content-Type':'multipart/form-data' } });
            setImageFile(null); setImagePreview(null); fetchTour();
        } catch { alert("Failed to save image."); }
    };

    const saveStop = async () => {
        try {
            const payload = { ...editStop.data, offerId: id, type: 'TOUR',
                stopOrder: editStop.index === -1 ? (tour.stops?.length || 0) : editStop.index };
            const fd = new FormData();
            fd.append('stop', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
            if (stopImageFile) fd.append('image', stopImageFile);
            const opts = { headers: { 'Content-Type': 'multipart/form-data' } };
            if (payload.id) await axios.put(`/stops/edit/${payload.id}`, fd, opts);
            else await axios.post('/stops/stop', fd, opts);
            setEditStop({ index: null, data: null });
            setStopImageFile(null);
            setStopImagePreview(null);
            fetchTour();
        } catch { alert("Failed to save stop."); }
    };

    const deleteStop = async (stopId) => {
        if (!stopId) return;
        try { await axios.delete(`/stops/delete/${stopId}`); setEditStop({ index: null, data: null }); fetchTour(); }
        catch { alert("Failed to delete stop."); }
    };

    const saveSlot = async () => {
        try {
            const d = editSlot.data;
            const payload = {
                ...d, tourOfferId: id, tourOffer: { id },
                startTime: fmt(d.startTime) + ':00',
                endTime:   fmt(d.endTime)   + ':00',
                activeDays: typeof d.activeDays === 'number' ? d.activeDays : 127,
            };
            if (payload.id) await axios.put(`/tour_slots/edit/${payload.id}`, payload);
            else await axios.post(`/tour_slots/tour_slot`, payload);
            setEditSlot({ index: null, data: null }); fetchTour();
        } catch { alert("Failed to save slot."); }
    };

    const deleteSlot = async (slotId) => {
        if (!slotId) return;
        try { await axios.delete(`/tour_slots/delete/${slotId}`); fetchTour(); }
        catch { alert("Failed to delete slot."); }
    };

    const savePrice = async () => {
        try {
            // Manager types a decimal (e.g. 20.00) → convert to cents before saving
            const priceInCents = amountToCents(parseFloat(editPrice.data.price) || 0, tour?.currency || 'EUR');
            const payload = { ...editPrice.data, price: priceInCents, tourOfferId: id, tourOffer: { id } };
            if (payload.id) await axios.put(`/tour_price/edit/${payload.id}`, payload);
            else await axios.post(`/tour_price/tour_price`, payload);
            setEditPrice({ index: null, data: null }); fetchTour();
        } catch { alert("Failed to save price."); }
    };

    const handleDeleteTour = async () => {
        setDeleteLoading(true); setDeleteError('');
        try {
            await axiosPublic.post('/api/app_user/login', { email: user.email, password: deletePassword });
        } catch {
            setDeleteError('Incorrect password. Please try again.');
            setDeleteLoading(false);
            return;
        }
        try {
            const res = await axios.delete(`/api/tourOffers/delete/${id}`);
            if (res.status === 200) {
                // Has bookings — was deactivated instead of deleted
                setTour(res.data);
                setDeleteOpen(false);
                setDeactivatedMsg('This offer has existing bookings and cannot be permanently deleted. It has been set to Inactive.');
            } else {
                navigate('/');
            }
        } catch {
            setDeleteError('Failed to delete the tour. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const canChangeStatus = isManager && tour &&
        (user?.id === tour?.createdById || user?.role === 'ADMIN');

    const handleStatusChange = async (newStatus) => {
        setStatusLoading(true);
        try {
            const res = await axios.patch(`/api/tourOffers/status/${id}?status=${newStatus}`);
            setTour(res.data);
            setDeactivatedMsg('');
        } catch {
            alert('Failed to change status. You may not have permission.');
        } finally {
            setStatusLoading(false);
        }
    };

    const editableSx = isManager ? {
        cursor: 'pointer', borderRadius: 1, transition: '0.2s',
        '&:hover': { backgroundColor: 'rgba(196,98,45,0.08)', outline: `1px dashed ${TERRACOTTA}` },
        padding: '4px 8px', margin: '-4px -8px'
    } : {};

    const startMainEdit = (field, val) => { if (!isManager) return; setActiveMainEdit(field); setTempMainValue(val); };

    const renderTypeIcon = (type) => {
        const sx = { fontSize: 40, color: TERRACOTTA };
        switch (type) {
            case "BUS":  return <DirectionsBus  sx={sx}/>;
            case "WALK": return <DirectionsWalk sx={sx}/>;
            case "BIKE": return <DirectionsBike sx={sx}/>;
            default:     return <DirectionsBus  sx={sx}/>;
        }
    };

    if (loading) return <Box display="flex" justifyContent="center" py={10}><CircularProgress color="primary"/></Box>;
    if (error)   return <Typography color="error" align="center">{error}</Typography>;

    const totalAdults = booking.local.ADULT.normal + booking.local.ADULT.electric + booking.foreign.ADULT.normal + booking.foreign.ADULT.electric;
    const canReserve = selectedDate && selectedSlot && totalAdults >= 1;
    const coverImage = imagePreview || tour.imageUrl || '';

    /* ── Detail items for the 2×2 grid ── */
    const detailItems = [
        { label: t('tour.detail_duration'),    icon: <TimeIcon sx={{ color: TERRACOTTA, fontSize: 22 }}/>,    key: 'duration',     val: tour.duration },
        { label: t('tour.detail_group'),  icon: <GroupIcon sx={{ color: FOREST, fontSize: 22 }}/>,       key: 'maxGroupSize', val: `${tour.maxGroupSize} ${t('tour.guests')}` },
        { label: t('tour.detail_start'), icon: <MapIcon sx={{ color: TERRACOTTA, fontSize: 22 }}/>,     key: 'startPoint',   val: tour.startPoint },
        { label: t('tour.detail_end'),   icon: <EndPointIcon sx={{ color: FOREST, fontSize: 22 }}/>,    key: 'endPoint',     val: tour.endPoint || t('common.unknown') },
    ];

    const tourUrl = `https://www.haytour.es/tours/${id}`;
    const tourDescription = tour.description
        ? tour.description.slice(0, 155)
        : `${tour.title} — a ${tour.type?.toLowerCase() || ''} tour in ${tour.place || 'Armenia'} with HayTour.`;

    return (
        <Box sx={{ position: 'relative', minHeight: '100vh', bgcolor: PARCHMENT }}>
            <Helmet>
                <title>{tour.title} — HayTour</title>
                <meta name="description" content={tourDescription} />
                <link rel="canonical" href={tourUrl} />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={tourUrl} />
                <meta property="og:title" content={`${tour.title} — HayTour`} />
                <meta property="og:description" content={tourDescription} />
                {tour.imageUrl && <meta property="og:image" content={tour.imageUrl} />}

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${tour.title} — HayTour`} />
                <meta name="twitter:description" content={tourDescription} />
                {tour.imageUrl && <meta name="twitter:image" content={tour.imageUrl} />}

                {/* JSON-LD: TouristAttraction */}
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "TouristAttraction",
                    "name": tour.title,
                    "description": tourDescription,
                    "url": tourUrl,
                    "image": tour.imageUrl || undefined,
                    "touristType": tour.type,
                    "location": {
                        "@type": "Place",
                        "name": tour.place || "Armenia",
                        "address": { "@type": "PostalAddress", "addressCountry": "AM" }
                    },
                    "offers": tour.basePrice ? {
                        "@type": "Offer",
                        "price": tour.basePrice,
                        "priceCurrency": "USD",
                        "url": tourUrl
                    } : undefined
                })}</script>
            </Helmet>
            {/* Watermark */}
            <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                width: '100%', height: '100%', opacity: 0.06, zIndex: 0, pointerEvents: 'none',
                backgroundImage: `url('https://i.assetzen.net/i/P6kr9CyK1FUZ/w:auto/h:400/q:70.jpg')`,
                backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain' }}/>

            <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
                {deactivatedMsg && (
                    <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDeactivatedMsg('')}>
                        {deactivatedMsg}
                    </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Button startIcon={<ArrowBackIcon/>} onClick={() => navigate(-1)}
                            sx={{ color: TERRACOTTA, fontWeight: 'bold' }}>
                        {t('tour.back')}
                    </Button>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {isManager && tour?.status && (
                            <Chip
                                label={tour.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                color={tour.status === 'ACTIVE' ? 'success' : 'default'}
                                size="small" sx={{ fontWeight: 700 }}
                            />
                        )}
                        {canChangeStatus && (
                            <Button variant="outlined" size="small"
                                onClick={() => handleStatusChange(tour.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                                disabled={statusLoading}
                                sx={{ borderRadius: 2, fontWeight: 700,
                                    color: tour?.status === 'ACTIVE' ? '#ed6c02' : '#2e7d32',
                                    borderColor: tour?.status === 'ACTIVE' ? '#ed6c02' : '#2e7d32',
                                    '&:hover': { bgcolor: tour?.status === 'ACTIVE' ? 'rgba(237,108,2,0.06)' : 'rgba(46,125,50,0.06)' }
                                }}>
                                {statusLoading ? '…' : tour?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                            </Button>
                        )}
                        {isManager && (
                            <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
                                onClick={() => { setDeleteOpen(true); setDeletePassword(''); setDeleteError(''); }}
                                sx={{ borderColor: '#d32f2f', color: '#d32f2f', borderRadius: 2, fontWeight: 700,
                                    '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' } }}>
                                Delete Tour
                            </Button>
                        )}
                    </Stack>
                </Box>

                {/* ── Title row ─────────────────────────────────────────────── */}
                <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                    <Box sx={editableSx} onClick={e => isManager && setIconAnchor(e.currentTarget)}>
                        {renderTypeIcon(tour.type)}
                    </Box>
                    <Menu anchorEl={iconAnchor} open={Boolean(iconAnchor)} onClose={() => setIconAnchor(null)}>
                        <MenuItem onClick={() => saveMainField('type','BUS')}><DirectionsBus sx={{ mr: 1 }}/> Bus</MenuItem>
                        <MenuItem onClick={() => saveMainField('type','WALK')}><DirectionsWalk sx={{ mr: 1 }}/> Walk</MenuItem>
                        <MenuItem onClick={() => saveMainField('type','BIKE')}><DirectionsBike sx={{ mr: 1 }}/> Bike</MenuItem>
                    </Menu>
                    <Box sx={{ flexGrow: 1 }}>
                        {activeMainEdit === 'title' ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField fullWidth variant="outlined" value={tempMainValue}
                                           onChange={e => setTempMainValue(e.target.value)}
                                           inputProps={{ style: { fontSize: 32, fontWeight: 'bold' } }}/>
                                <IconButton color="success" onClick={() => saveMainField('title', tempMainValue)}><SaveIcon/></IconButton>
                                <IconButton color="error"   onClick={() => setActiveMainEdit(null)}><CloseIcon/></IconButton>
                            </Stack>
                        ) : (
                            <Typography variant="h3" fontWeight="800" sx={{ ...editableSx, color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}
                                        onClick={() => startMainEdit('title', tour.title)}>
                                {tour.title}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                {/* ── Region (place) ── */}
                <Box sx={{ mb: 1, ml: 0.5 }}>
                    {activeMainEdit === 'place' ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: 400 }}>
                            <MapIcon sx={{ color: TERRACOTTA, fontSize: 20 }}/>
                            <TextField size="small" variant="standard" value={tempMainValue}
                                       onChange={e => setTempMainValue(e.target.value)}
                                       placeholder="Region / Place"
                                       sx={{ flex: 1 }}/>
                            <IconButton size="small" color="success" onClick={() => saveMainField('place', tempMainValue)}><SaveIcon fontSize="small"/></IconButton>
                            <IconButton size="small" color="error" onClick={() => setActiveMainEdit(null)}><CloseIcon fontSize="small"/></IconButton>
                        </Stack>
                    ) : (
                        <Stack direction="row" alignItems="center" spacing={0.8}
                               sx={{ ...editableSx, display: 'inline-flex' }}
                               onClick={() => startMainEdit('place', tour.place || '')}>
                            <MapIcon sx={{ color: TERRACOTTA, fontSize: 20 }}/>
                            <Typography variant="subtitle1" sx={{ color: WALNUT, fontWeight: 600, opacity: tour.place ? 1 : 0.45 }}>
                                {tour.place || (isManager ? 'Click to add region' : '')}
                            </Typography>
                        </Stack>
                    )}
                </Box>

                {/* ── Rating summary ── */}
                {ratingSummary && ratingSummary.ratingCount > 0 && (
                    <Box sx={{ mb: 1.5, ml: 0.5, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        {[1,2,3,4,5].map(n => {
                            const avg = ratingSummary.averageScore || 0;
                            const filled = n <= Math.round(avg);
                            return filled
                                ? <Box key={n} component="span" sx={{ color: GOLD, fontSize: '1.1rem', lineHeight: 1 }}>★</Box>
                                : <Box key={n} component="span" sx={{ color: 'rgba(0,0,0,0.2)', fontSize: '1.1rem', lineHeight: 1 }}>★</Box>;
                        })}
                        <Typography variant="body2" fontWeight={700} sx={{ color: GOLD }}>
                            {ratingSummary.averageScore?.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            ({ratingSummary.ratingCount} review{ratingSummary.ratingCount !== 1 ? 's' : ''})
                        </Typography>
                    </Box>
                )}

                {/* ── Base Price (manager only) ── */}
                {isManager && (
                    <Box sx={{ mb: 2, ml: 0.5 }}>
                        {activeMainEdit === 'basePrice' ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: 300 }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ color: TERRACOTTA }}>Base Price</Typography>
                                <TextField size="small" variant="standard" type="number" value={tempMainValue}
                                           onChange={e => setTempMainValue(e.target.value)}
                                           inputProps={{ min: 0, step: 0.01 }}
                                           sx={{ width: 100 }}/>
                                <IconButton size="small" color="success" onClick={() => saveMainField('basePrice', amountToCents(parseFloat(tempMainValue) || 0, tour.currency || 'EUR'))}><SaveIcon fontSize="small"/></IconButton>
                                <IconButton size="small" color="error" onClick={() => setActiveMainEdit(null)}><CloseIcon fontSize="small"/></IconButton>
                            </Stack>
                        ) : (
                            <Stack direction="row" alignItems="center" spacing={0.8}
                                   sx={{ ...editableSx, display: 'inline-flex' }}
                                   onClick={() => startMainEdit('basePrice', centsToAmount(tour.basePrice || 0, tour.currency || 'EUR'))}>
                                <Typography variant="body2" sx={{ color: FOREST, fontWeight: 700 }}>
                                    Base Price: {formatMoney(tour.basePrice || 0, tour.currency || 'EUR')}
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                )}


                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 4,
                    flexDirection: { xs: 'column', md: 'row' } }}>

                    {/* ═══ LEFT COLUMN ═══ */}
                    <Box sx={{ width: { xs: '100%', md: '48%' }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>

                        {/* ── Cover Photo ── */}
                        <Box sx={{ position: 'relative' }}>
                            <Paper elevation={4} sx={{
                                width: '100%', paddingTop: '75%', /* ~3:2 ratio */
                                position: 'relative',
                                backgroundImage: `url(${coverImage})`,
                                backgroundSize: 'cover', backgroundPosition: 'center',
                                borderRadius: 2, overflow: 'hidden', border: `3px solid ${STONE}`,
                                cursor: isManager ? 'pointer' : 'default',
                                '&:hover .img-ov': { opacity: isManager ? 1 : 0 }
                            }} onClick={() => isManager && !imageFile && fileInputRef.current.click()}>
                                {!imageFile && isManager && (
                                    <Box className="img-ov" sx={{ position: 'absolute', inset: 0,
                                        bgcolor: 'rgba(196,98,45,0.45)', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        opacity: 0, transition: '0.3s' }}>
                                        <Typography variant="h6" color="white" fontWeight="bold">{t('tour.click_image')}</Typography>
                                    </Box>
                                )}
                            </Paper>
                            <input type="file" hidden ref={fileInputRef}
                                   onChange={e => { const f = e.target.files[0]; if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); } }}/>
                            {imageFile && (
                                <Paper sx={{ position: 'absolute', bottom: 16, right: 16, p: 1, display: 'flex', gap: 1, borderRadius: 2, bgcolor: '#fff' }}>
                                    <Button variant="contained" onClick={saveImage} startIcon={<SaveIcon/>}>{t('tour.save_image')}</Button>
                                    <IconButton onClick={() => { setImageFile(null); setImagePreview(null); }}><CloseIcon/></IconButton>
                                </Paper>
                            )}
                        </Box>

                        {/* ── Tour Details Card (2×2 grid) ── */}
                        <Paper elevation={0} sx={{
                            bgcolor: '#FFFFFF', borderRadius: 2,
                            borderLeft: `6px solid ${TERRACOTTA}`,
                            boxShadow: '0 2px 12px rgba(44,31,20,0.06)',
                            p: 2.5,
                        }}>
                            <Grid container spacing={2}>
                                {detailItems.map(item => (
                                    <Grid item xs={6} key={item.key}>
                                        <Stack spacing={0.5}>
                                            <Stack direction="row" alignItems="center" spacing={0.8}>
                                                {item.icon}
                                                <Typography variant="caption" color="textSecondary" fontWeight="900"
                                                            sx={{ letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                                                    {item.label}
                                                </Typography>
                                            </Stack>
                                            {activeMainEdit === item.key ? (
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <TextField size="small" variant="standard" value={tempMainValue}
                                                               onChange={e => setTempMainValue(e.target.value)}
                                                               sx={{ flex: 1 }}/>
                                                    <IconButton size="small" color="success" onClick={() => saveMainField(item.key, tempMainValue)}><SaveIcon fontSize="small"/></IconButton>
                                                    <IconButton size="small" color="error" onClick={() => setActiveMainEdit(null)}><CloseIcon fontSize="small"/></IconButton>
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" fontWeight="bold" sx={{ ...editableSx, pl: 0.5 }}
                                                            onClick={() => startMainEdit(item.key, tour[item.key])}>
                                                    {item.val}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>

                        {/* ── Description ── */}
                        <Box>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: TERRACOTTA, mb: 1.5 }}>
                                {t('tour.about')}
                            </Typography>
                            {activeMainEdit === 'description' ? (
                                <Box>
                                    <TextField multiline fullWidth variant="outlined" rows={12} value={tempMainValue}
                                               onChange={e => setTempMainValue(e.target.value)}/>
                                    <Stack direction="row" spacing={1} mt={2}>
                                        <Button variant="contained" color="success" onClick={() => saveMainField('description', tempMainValue)}>Update</Button>
                                        <Button onClick={() => setActiveMainEdit(null)}>Cancel</Button>
                                    </Stack>
                                </Box>
                            ) : (
                                <Box sx={{ ...editableSx }} onClick={() => startMainEdit('description', tour.description)}>
                                    {tour.description
                                        ? <ReactMarkdown components={{
                                            p:  (props) => <Typography variant="body1" paragraph sx={{ color: '#4A3728', lineHeight: 1.8 }} {...props} />,
                                            h1: (props) => <Typography variant="h5" fontWeight={700} sx={{ color: WALNUT, mt: 2, mb: 1 }} {...props} />,
                                            h2: (props) => <Typography variant="h6" fontWeight={700} sx={{ color: WALNUT, mt: 1.5, mb: 0.8 }} {...props} />,
                                            h3: (props) => <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT, mt: 1, mb: 0.5 }} {...props} />,
                                          }}>{tour.description}</ReactMarkdown>
                                        : isManager && <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Click to add description</Typography>}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* ═══ RIGHT COLUMN — RESERVATION CARD ═══ */}
                    <Card elevation={0} sx={{
                        width: { xs: '100%', md: '58%' },
                        flexShrink: 1,
                        borderRadius: 3,
                        border: `1px solid rgba(196,98,45,0.18)`,
                        bgcolor: '#FFFFFF',
                        boxShadow: `0 8px 32px rgba(196,98,45,0.08)`,
                        alignSelf: 'flex-start',
                    }}>
                        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: TERRACOTTA, mb: 2 }}>
                                {t('tour.reservation')}
                            </Typography>

                            {/* Manager slot management strip */}
                            {isManager && (
                                <Box mb={2}>
                                    <Typography variant="caption" fontWeight="900" color="textSecondary" sx={{ letterSpacing: '0.1em' }}>
                                        MANAGE SLOTS
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.8} gap={0.8}>
                                        {(tour.slots||[]).map((slot, index) => (
                                            editSlot.index === index ? (
                                                <Paper key={index} sx={{ p: 1.5, border: `1px solid ${TERRACOTTA}`, bgcolor: PARCHMENT, minWidth: 200 }}>
                                                    <Stack spacing={0.8}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography variant="caption" sx={{ minWidth: 36 }}>Start:</Typography>
                                                            <input type="time" value={fmt(editSlot.data.startTime)}
                                                                   onChange={e => setEditSlot({...editSlot, data: {...editSlot.data, startTime: e.target.value}})}/>
                                                        </Stack>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography variant="caption" sx={{ minWidth: 36 }}>End:</Typography>
                                                            <input type="time" value={fmt(editSlot.data.endTime)}
                                                                   onChange={e => setEditSlot({...editSlot, data: {...editSlot.data, endTime: e.target.value}})}/>
                                                        </Stack>
                                                        <Box>
                                                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Days:</Typography>
                                                            <DayPicker value={editSlot.data.activeDays ?? 127}
                                                                       onChange={v => setEditSlot({...editSlot, data: {...editSlot.data, activeDays: v}})}/>
                                                        </Box>
                                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            <IconButton size="small" color="success" onClick={saveSlot}><SaveIcon fontSize="small"/></IconButton>
                                                            <IconButton size="small" color="error" onClick={() => deleteSlot(slot.id)}><DeleteIcon fontSize="small"/></IconButton>
                                                            <IconButton size="small" onClick={() => setEditSlot({index: null, data: null})}><CloseIcon fontSize="small"/></IconButton>
                                                        </Stack>
                                                    </Stack>
                                                </Paper>
                                            ) : (
                                                <Chip key={slot.id||index}
                                                      label={`${fmt(slot.startTime)} – ${fmt(slot.endTime)}  ·  ${daysSummary(slot.activeDays ?? 127)}`}
                                                      variant="outlined"
                                                      sx={{ borderColor: TERRACOTTA, color: TERRACOTTA, fontWeight: 'bold',
                                                          '&:hover': { bgcolor: 'rgba(196,98,45,0.08)' } }}
                                                      onClick={() => setEditSlot({index, data: {...slot, activeDays: typeof slot.activeDays === 'number' ? slot.activeDays : 127}})}/>
                                            )
                                        ))}
                                        {editSlot.index === -1 && (
                                            <Paper sx={{ p: 1.5, border: `2px dashed ${TERRACOTTA}`, bgcolor: '#fff', minWidth: 200 }}>
                                                <Stack spacing={0.8}>
                                                    <Typography variant="caption" fontWeight="bold" sx={{ color: TERRACOTTA }}>NEW SLOT</Typography>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography variant="caption" sx={{ minWidth: 36 }}>Start:</Typography>
                                                        <input type="time" value={editSlot.data.startTime}
                                                               onChange={e => setEditSlot({...editSlot, data: {...editSlot.data, startTime: e.target.value}})}
                                                               style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 5px' }}/>
                                                    </Stack>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography variant="caption" sx={{ minWidth: 36 }}>End:</Typography>
                                                        <input type="time" value={editSlot.data.endTime}
                                                               onChange={e => setEditSlot({...editSlot, data: {...editSlot.data, endTime: e.target.value}})}
                                                               style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 5px' }}/>
                                                    </Stack>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Days:</Typography>
                                                        <DayPicker value={editSlot.data.activeDays ?? 127}
                                                                   onChange={v => setEditSlot({...editSlot, data: {...editSlot.data, activeDays: v}})}/>
                                                    </Box>
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <IconButton size="small" color="success" onClick={saveSlot}><SaveIcon fontSize="small"/></IconButton>
                                                        <IconButton size="small" onClick={() => setEditSlot({index: null, data: null})}><CloseIcon fontSize="small"/></IconButton>
                                                    </Stack>
                                                </Stack>
                                            </Paper>
                                        )}
                                        {editSlot.index === null && (
                                            <Chip icon={<AddIcon sx={{ fontSize: '1rem !important' }}/>} label="Add slot"
                                                  variant="outlined" onClick={() => setEditSlot({index: -1, data: {startTime: "09:00", endTime: "12:00", activeDays: 127}})}
                                                  sx={{ borderColor: TERRACOTTA, color: TERRACOTTA, borderStyle: 'dashed' }}/>
                                        )}
                                    </Stack>
                                    <Divider sx={{ mt: 2, mb: 0 }}/>
                                </Box>
                            )}

                            {/* ── 2-COLUMN INTERIOR ── */}
                            <Grid container spacing={2} alignItems="flex-start">

                                {/* COL 1: Choose Date + time chips */}
                                <Grid item xs={12} md={5}
                                      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Typography variant="caption" fontWeight="900" color="textSecondary"
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, letterSpacing: '0.1em' }}>
                                        <CalIcon sx={{ fontSize: '0.9rem' }}/> {t('tour.choose_date_label')}
                                    </Typography>
                                    <MiniCalendar
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        activeDays={tourActiveDays || null}
                                    />
                                    {selectedDate && (
                                        <Box>
                                            <Typography variant="caption" fontWeight="900" color="textSecondary"
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, letterSpacing: '0.1em', mb: 0.8 }}>
                                                <TimeIcon sx={{ fontSize: '0.9rem' }}/> {t('tour.choose_time_label')}
                                            </Typography>
                                            {availableSlots.length === 0 ? (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    {t('tour.no_slots')}
                                                </Typography>
                                            ) : (
                                                <Stack spacing={0.8}>
                                                    {availableSlots.map(slot => (
                                                        <Chip key={slot.id}
                                                              label={`${fmt(slot.startTime)} – ${fmt(slot.endTime)}`}
                                                              variant={selectedSlot?.id === slot.id ? "filled" : "outlined"}
                                                              onClick={() => setSelectedSlot(slot)}
                                                              sx={{
                                                                  bgcolor: selectedSlot?.id === slot.id ? TERRACOTTA : 'transparent',
                                                                  color: selectedSlot?.id === slot.id ? 'white' : TERRACOTTA,
                                                                  borderColor: TERRACOTTA, fontWeight: 'bold',
                                                                  '&:hover': { bgcolor: selectedSlot?.id === slot.id ? '#9A4A1F' : 'rgba(196,98,45,0.08)' }
                                                              }}
                                                        />
                                                    ))}
                                                </Stack>
                                            )}
                                        </Box>
                                    )}
                                </Grid>

                                {/* COL 2: Rates card */}
                                <Grid item xs={12} md={7}>
                                    <Box sx={{
                                        border: '1px solid rgba(196,98,45,0.18)',
                                        borderRadius: 2.5,
                                        overflow: 'hidden',
                                        bgcolor: 'white',
                                    }}>
                                        {/* Card header */}
                                        <Box sx={{
                                            px: 2, py: 1.2,
                                            borderBottom: '1px solid rgba(196,98,45,0.12)',
                                            bgcolor: 'rgba(247,243,238,0.7)',
                                            display: 'flex', alignItems: 'center',
                                        }}>
                                            <Typography variant="caption" fontWeight="900" color="textSecondary"
                                                        sx={{ letterSpacing: '0.15em', flex: 1 }}>
                                                RATES
                                            </Typography>
                                            {tour.type === 'BIKE' && (
                                                <Stack direction="row" spacing={0} sx={{ mr: 0.5 }}>
                                                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                                                                sx={{ minWidth: 66, textAlign: 'center', fontSize: '0.65rem' }}>
                                                        NORMAL
                                                    </Typography>
                                                    <Typography variant="caption" fontWeight={700} sx={{ minWidth: 66, textAlign: 'center', fontSize: '0.65rem', color: FOREST }}>
                                                        E-BIKE
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Box>

                                        {/* Rates body — edge-to-edge rows, alternating shades */}
                                        {['local','foreign'].map((type, sectionIdx) => (
                                            <React.Fragment key={type}>
                                                {/* Narrow divider between Resident and Tourist */}
                                                {sectionIdx > 0 && <Divider sx={{ mx: 2 }} />}

                                                {/* Section label */}
                                                <Box sx={{ px: 2, pt: sectionIdx === 0 ? 1.5 : 1, pb: 0.5 }}>
                                                    <Typography variant="overline" fontWeight="900"
                                                                sx={{ color: type === 'local' ? TERRACOTTA : FOREST, display: 'block' }}>
                                                        {type === 'local' ? 'RESIDENT' : 'TOURIST'} RATES
                                                    </Typography>
                                                </Box>

                                                {/* Rate rows */}
                                                {['ADULT','CHILD','INFANT'].map((cat, rowIdx) => {
                                                    const isForeign = type === 'foreign';
                                                    const priceObj  = (tour.prices||[]).find(p => p.category === cat && p.isForeigner === isForeign);
                                                    const rowBg     = rowIdx % 2 === 0 ? 'white' : 'rgba(0,0,0,0.035)';
                                                    const catLabel  = { ADULT: t('tour.adult_label', 'Adult (18+)'), CHILD: t('tour.child_label', 'Child (7–17)'), INFANT: t('tour.infant_label', 'Infant (0–6)') }[cat];

                                                    if (isManager && editPrice.index === `${type}-${cat}`) {
                                                        const priceEmpty = editPrice.data.price === '' || editPrice.data.price == null;
                                                        return (
                                                            <Box key={cat} sx={{ px: 2, py: 0.9, bgcolor: rowBg }}>
                                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 90 }}>{catLabel}</Typography>
                                                                    <TextField
                                                                        size="small" type="number"
                                                                        sx={{ width: 160 }}
                                                                        value={editPrice.data.price}
                                                                        error={priceEmpty}
                                                                        helperText={priceEmpty ? 'Enter a price' : ''}
                                                                        onChange={e => setEditPrice({...editPrice, data: {...editPrice.data, price: e.target.value}})}
                                                                    />
                                                                    <IconButton size="small" color="success"
                                                                                disabled={priceEmpty}
                                                                                onClick={savePrice}>
                                                                        <SaveIcon fontSize="small"/>
                                                                    </IconButton>
                                                                    <IconButton size="small" color="error" onClick={() => setEditPrice({index: null, data: null})}><CloseIcon fontSize="small"/></IconButton>
                                                                </Stack>
                                                            </Box>
                                                        );
                                                    }

                                                    return (
                                                        <Box key={cat} sx={{ px: 2, py: 0.85, bgcolor: rowBg }}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Stack direction="row" alignItems="center" spacing={0.8} sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography variant="body2" fontWeight="bold" sx={{ ...editableSx }}
                                                                                onClick={() => isManager && setEditPrice({index: `${type}-${cat}`, data: priceObj ? { ...priceObj, price: centsToAmount(priceObj.price, tour.currency || 'EUR') } : {category: cat, isForeigner: isForeign, price: 0}})}>
                                                                        {catLabel}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ ...editableSx }}
                                                                                onClick={() => isManager && setEditPrice({index: `${type}-${cat}`, data: priceObj ? { ...priceObj, price: centsToAmount(priceObj.price, tour.currency || 'EUR') } : {category: cat, isForeigner: isForeign, price: 0}})}>
                                                                        {formatMoney(priceObj?.price || 0, tour.currency || 'EUR')}
                                                                    </Typography>
                                                                </Stack>
                                                                {tour.type === 'BIKE' ? (
                                                                    <Stack direction="row" spacing={0.5}>
                                                                        {/* Normal counter */}
                                                                        <Stack direction="row" alignItems="center" spacing={0.2}>
                                                                            <IconButton size="small" sx={{ p: 0.4 }} onClick={() => handleBookingCount(type, cat, 'normal', -1)}>
                                                                                <RemoveIcon sx={{ fontSize: '1.1rem' }}/>
                                                                            </IconButton>
                                                                            <Typography fontWeight="bold" sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.9rem' }}>
                                                                                {booking[type][cat].normal}
                                                                            </Typography>
                                                                            <IconButton size="small" sx={{ p: 0.4 }} onClick={() => handleBookingCount(type, cat, 'normal', 1)}>
                                                                                <AddQtyIcon sx={{ fontSize: '1.1rem' }}/>
                                                                            </IconButton>
                                                                        </Stack>
                                                                        <Box sx={{ width: 1, bgcolor: 'divider', mx: 0.5 }} />
                                                                        {/* Electric counter */}
                                                                        <Stack direction="row" alignItems="center" spacing={0.2}>
                                                                            <IconButton size="small" sx={{ p: 0.4, color: FOREST }} onClick={() => handleBookingCount(type, cat, 'electric', -1)}>
                                                                                <RemoveIcon sx={{ fontSize: '1.1rem' }}/>
                                                                            </IconButton>
                                                                            <Typography fontWeight="bold" sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.9rem', color: FOREST }}>
                                                                                {booking[type][cat].electric}
                                                                            </Typography>
                                                                            <IconButton size="small" sx={{ p: 0.4, color: FOREST }} onClick={() => handleBookingCount(type, cat, 'electric', 1)}>
                                                                                <AddQtyIcon sx={{ fontSize: '1.1rem' }}/>
                                                                            </IconButton>
                                                                        </Stack>
                                                                    </Stack>
                                                                ) : (
                                                                    <Stack direction="row" alignItems="center" spacing={0.2}>
                                                                        <IconButton size="small" sx={{ p: 0.4 }} onClick={() => handleBookingCount(type, cat, 'normal', -1)}>
                                                                            <RemoveIcon sx={{ fontSize: '1.1rem' }}/>
                                                                        </IconButton>
                                                                        <Typography fontWeight="bold" sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.9rem' }}>
                                                                            {booking[type][cat].normal}
                                                                        </Typography>
                                                                        <IconButton size="small" sx={{ p: 0.4 }} onClick={() => handleBookingCount(type, cat, 'normal', 1)}>
                                                                            <AddQtyIcon sx={{ fontSize: '1.1rem' }}/>
                                                                        </IconButton>
                                                                    </Stack>
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                    );
                                                })}

                                                {/* Bottom padding after Tourist section */}
                                                {sectionIdx === 1 && <Box sx={{ pb: 1 }} />}
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* ── Orange summary + reserve card ── */}
                            <Box sx={{
                                mt: 2, borderRadius: 2.5, overflow: 'hidden',
                                background: `linear-gradient(160deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                            }}>
                                {/* Summary rows — one per ticket line, alternating semi-transparent white */}
                                {summaryLines.map((line, i) => (
                                    <Box key={i} sx={{
                                        px: 2.5, py: 0.95,
                                        bgcolor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.08)',
                                        display: 'flex', alignItems: 'center',
                                    }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', fontWeight: 500 }}>
                                            {line.label}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2" fontWeight={700} sx={{ color:  'rgb(255,255,255)'}}>
                                            {formatMoney(line.amount, tour.currency || 'EUR')}
                                        </Typography>
                                    </Box>
                                ))}

                                {/* Separator (only when there are lines) */}
                                {summaryLines.length > 0 && (
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.22)' }} />
                                )}

                                {/* Total row */}
                                <Box sx={{ px: 2.5, py: 1.1, display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" fontWeight={700}
                                                sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: '0.73rem' }}>
                                        Total
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <Typography variant="h6" fontWeight={900} sx={{ color: 'rgb(255,255,255)', lineHeight: 1 }}>
                                        {formatMoney(total, tour.currency || 'EUR')}
                                    </Typography>
                                </Box>

                                {/* Separator */}
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.22)' }} />

                                {/* Warning + button */}
                                <Box sx={{ px: 1.5, pt: 1.2, pb: 1.5 }}>
                                    {selectedDate && selectedSlot && totalAdults < 1 && !isManager && (
                                        <Typography variant="caption" sx={{
                                            color: 'rgba(255,255,255,0.65)', display: 'block',
                                            textAlign: 'center', mb: 1,
                                        }}>
                                            {t('tour.need_adult', 'At least 1 adult is required to make a reservation.')}
                                        </Typography>
                                    )}
                                    <Button fullWidth variant="contained" onClick={handleGoToCheckout}
                                            disabled={!canReserve && !isManager}
                                            sx={{
                                                py: 1.4, fontWeight: '900', fontSize: '0.95rem',
                                                bgcolor: 'rgba(255,255,255,0.14)',
                                                color: 'white', borderRadius: 2,
                                                border: '1px solid rgba(255,255,255,0.28)',
                                                boxShadow: 'none',
                                                '&:hover': { bgcolor: 'rgba(255,255,255,0.24)', boxShadow: 'none' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.28)', border: '1px solid rgba(255,255,255,0.1)' },
                                            }}>
                                        {canReserve || isManager
                                            ? (isManager ? t('tour.process_booking') : t('tour.reserve'))
                                            : !selectedDate ? t('tour.choose_date_cta') : !selectedSlot ? t('tour.choose_time_cta') : t('tour.reserve')}
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>{/* end two-column hero */}

                {/* ══════════════════════════════════════════════════════════════
                    ITINERARY — full-width below the two-column hero
                ══════════════════════════════════════════════════════════════ */}
                {((tour.stops && tour.stops.length > 0) || isManager) && (
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h4" fontWeight="bold" mb={4} sx={{ color: WALNUT }}>
                            {t('tour.itinerary')}
                        </Typography>

                        <Stack spacing={0}>
                            {(tour.stops||[]).map((stop, index) => (
                                <Box key={stop.id || index}>
                                    {editStop.index === index ? (
                                        <Paper sx={{ p: 3, border: `1px solid ${TERRACOTTA}`, borderRadius: 2, mb: 3 }}>
                                            <TextField size="small" fullWidth label="Stop title" variant="standard" sx={{ mb: 2 }}
                                                       value={editStop.data?.title || ""}
                                                       onChange={e => setEditStop({...editStop, data: {...editStop.data, title: e.target.value}})}/>
                                            <TextField size="small" fullWidth label="Description" variant="outlined" multiline rows={4}
                                                       value={editStop.data?.description || ""}
                                                       onChange={e => setEditStop({...editStop, data: {...editStop.data, description: e.target.value}})}/>
                                            <Box mt={2}>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>Stop image</Typography>
                                                {(stopImagePreview || editStop.data?.imageUrl) && (
                                                    <Box component="img"
                                                         src={stopImagePreview || editStop.data.imageUrl}
                                                         alt="preview"
                                                         sx={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                                                )}
                                                <Button variant="outlined" size="small" component="label"
                                                        sx={{ borderColor: TERRACOTTA, color: TERRACOTTA }}>
                                                    {stopImagePreview ? 'Change image' : 'Upload image'}
                                                    <input type="file" hidden accept="image/*"
                                                           onChange={e => { const f = e.target.files[0]; if (f) { setStopImageFile(f); setStopImagePreview(URL.createObjectURL(f)); } }} />
                                                </Button>
                                                {stopImagePreview && (
                                                    <Button size="small" sx={{ ml: 1 }}
                                                            onClick={() => { setStopImageFile(null); setStopImagePreview(null); }}>Remove</Button>
                                                )}
                                            </Box>
                                            <Stack direction="row" spacing={1} mt={2}>
                                                <Button size="small" variant="contained" onClick={saveStop}>Save</Button>
                                                <Button size="small" onClick={() => { setEditStop({index: null, data: null}); setStopImageFile(null); setStopImagePreview(null); }}>Cancel</Button>
                                                <IconButton color="error" onClick={() => deleteStop(stop.id)} sx={{ ml: 'auto' }}><DeleteIcon/></IconButton>
                                            </Stack>
                                        </Paper>
                                    ) : (
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}
                                               sx={{
                                                   py: 4,
                                                   bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(237,232,225,0.45)',
                                                   borderRadius: 2, px: 2,
                                               }}>
                                            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
                                                <Box sx={{
                                                    flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
                                                    bgcolor: TERRACOTTA, display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center', mt: 0.3,
                                                    boxShadow: '0 2px 8px rgba(196,98,45,0.35)'
                                                }}>
                                                    <Typography fontWeight={900} sx={{ color: 'white', fontSize: '0.85rem' }}>
                                                        {index + 1}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="h6" fontWeight="bold"
                                                                sx={{ color: WALNUT, mb: 1, ...editableSx }}
                                                                onClick={() => isManager && setEditStop({index, data: stop})}>
                                                        {stop.title}
                                                    </Typography>
                                                    <Typography variant="body1" color="textSecondary"
                                                                sx={{ lineHeight: 1.8, ...editableSx }}
                                                                onClick={() => isManager && setEditStop({index, data: stop})}>
                                                        {stop.description}
                                                    </Typography>
                                                    {isManager && (
                                                        <Button size="small" sx={{
                                                            mt: 1, color: TERRACOTTA, textTransform: 'none',
                                                            p: 0, fontSize: '0.78rem',
                                                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                                        }}
                                                                onClick={() => setEditStop({index, data: stop})}>
                                                            Edit stop
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Stack>
                                            <Box sx={{ width: { xs: '100%', md: 600 }, flexShrink: 0 }}>
                                                <Box sx={{
                                                    paddingTop: '56.25%',
                                                    position: 'relative', borderRadius: 2,
                                                    overflow: 'hidden',
                                                    boxShadow: '0 4px 20px rgba(44,31,20,0.12)',
                                                }}>
                                                    <Box component="img" src={stop.imageUrl || coverImage} alt={stop.title}
                                                         sx={{
                                                             position: 'absolute', inset: 0,
                                                             width: '100%', height: '100%',
                                                             objectFit: 'cover', objectPosition: 'center',
                                                             filter: `brightness(${0.82 + (index % 3) * 0.06})`,
                                                         }}/>
                                                    <Box sx={{
                                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                                        background: 'linear-gradient(to top, rgba(44,31,20,0.75) 0%, transparent 100%)',
                                                        px: 2, py: 1.5,
                                                    }}>
                                                        <Typography variant="caption"
                                                                    sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.75rem' }}>
                                                            Stop {index + 1} · {stop.title}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Stack>
                                    )}
                                </Box>
                            ))}

                            {isManager && editStop.index === -1 && (
                                <Paper sx={{ p: 3, border: '2px dashed #4A8C5C', borderRadius: 2, bgcolor: '#f6faf7', mt: 2 }}>
                                    <Typography variant="subtitle2" color="success.main" fontWeight="bold" mb={2}>New Stop</Typography>
                                    <TextField size="small" fullWidth label="Stop title" variant="standard" sx={{ mb: 2 }}
                                               value={editStop.data?.title || ""}
                                               onChange={e => setEditStop({...editStop, data: {...editStop.data, title: e.target.value}})}/>
                                    <TextField size="small" fullWidth label="Description" variant="outlined" multiline rows={3}
                                               value={editStop.data?.description || ""}
                                               onChange={e => setEditStop({...editStop, data: {...editStop.data, description: e.target.value}})}/>
                                    <Box mt={2}>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>Stop image</Typography>
                                        {stopImagePreview && (
                                            <Box component="img" src={stopImagePreview} alt="preview"
                                                 sx={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                                        )}
                                        <Button variant="outlined" size="small" component="label"
                                                sx={{ borderColor: FOREST, color: FOREST }}>
                                            {stopImagePreview ? 'Change image' : 'Upload image'}
                                            <input type="file" hidden accept="image/*"
                                                   onChange={e => { const f = e.target.files[0]; if (f) { setStopImageFile(f); setStopImagePreview(URL.createObjectURL(f)); } }} />
                                        </Button>
                                        {stopImagePreview && (
                                            <Button size="small" sx={{ ml: 1 }}
                                                    onClick={() => { setStopImageFile(null); setStopImagePreview(null); }}>Remove</Button>
                                        )}
                                    </Box>
                                    <Stack direction="row" spacing={1} mt={2}>
                                        <Button size="small" variant="contained" color="success" onClick={saveStop}>Create</Button>
                                        <Button size="small" onClick={() => { setEditStop({index: null, data: null}); setStopImageFile(null); setStopImagePreview(null); }}>Cancel</Button>
                                    </Stack>
                                </Paper>
                            )}

                            {isManager && editStop.index === null && (
                                <Button startIcon={<AddIcon/>} variant="outlined" sx={{ mt: 3, color: TERRACOTTA, borderColor: TERRACOTTA }}
                                        onClick={() => setEditStop({index: -1, data: {title: "", description: ""}})}>
                                    Insert New Stop
                                </Button>
                            )}
                        </Stack>
                    </Box>
                )}

                {/* Delete Tour Dialog */}
                <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)}
                    maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle fontWeight={700} sx={{ color: '#b71c1c', fontFamily: "'Playfair Display', Georgia, serif" }}>
                        Delete Tour
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#2C1F14' }}>
                                You are about to delete <strong>{tour?.title}</strong>.
                                If this offer has existing bookings it will be <strong>deactivated</strong> instead of permanently deleted.
                                Please enter your password to confirm.
                            </Typography>
                            <TextField
                                fullWidth size="small" type="password" label="Your password"
                                value={deletePassword}
                                onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
                                onKeyDown={e => e.key === 'Enter' && !deleteLoading && handleDeleteTour()}
                                error={!!deleteError}
                                helperText={deleteError}
                                autoFocus
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                        <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
                        <Button variant="contained" color="error" onClick={handleDeleteTour}
                            disabled={deleteLoading || !deletePassword}
                            sx={{ borderRadius: 2, fontWeight: 700 }}>
                            {deleteLoading ? 'Deleting…' : 'Delete Permanently'}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Container>
        </Box>
    );
}
